import { useCallback, useRef, useState } from "react";
import { errorHandler } from "../APIs/errorHandler";
import {
  fetchOrganisationById,
  fetchOrganisations,
  OrganisationOption
} from "../APIs/Organisation/GetOrganisations";
import { fetchVenueGroupConfig } from "../APIs/Venue/GetVenueGroupConfig";
import AccessDeniedContent from "../components/AccessDeniedContent";
import OrganisationSelectionContent from "../components/OrganisationSelectionContent";
import { useInfoModal } from "../context/InfoModalContext";
import { useLoadingStatus } from "../context/LoadingStatusContext";
import { dmnIdentity } from "../utils/dmnIdentity";
import { useAppContext } from "./useAppContext";

const useManageOrgData = () => {
  const { setVenueGroupConfig, setVenueUserConfig } = useAppContext();
  const { showInfoModal, hideInfoModal } = useInfoModal();
  const { updateStage } = useLoadingStatus();
  const [isLoading, setIsLoading] = useState(false);
  const loadingRef = useRef(false);

  const handleOrganisationSelect = useCallback(
    async (organisationId: string) => {
      hideInfoModal();
      setIsLoading(true);
      updateStage("organisation", "loading");

      try {
        const organisationConfig = await fetchOrganisationById(organisationId);

        if (organisationConfig?.venueGroup?._id) {
          setVenueGroupConfig(organisationConfig.venueGroup);
          updateStage("organisation", "success");
        } else {
          updateStage("organisation", "error", {
            message: "Failed to load organisation configuration.",
            technicalDetails:
              "fetchOrganisationById returned null or missing venueGroup._id"
          });
          noOrganisationsAvailable();
          errorHandler(
            new Error("Failed to fetch organisation configuration"),
            "Error loading organisation"
          );
        }
      } catch (error) {
        updateStage("organisation", "error", {
          message: "Error loading organisation configuration.",
          technicalDetails:
            error instanceof Error ? error.message : String(error)
        });
        errorHandler(error, "Error loading organisation");
      } finally {
        setIsLoading(false);
      }
    },
    [hideInfoModal, setVenueGroupConfig, updateStage]
  );

  const showOrganisationSelectionModal = useCallback(
    (organisations: OrganisationOption[]) => {
      showInfoModal({
        title: "Select Organisation",
        children: (
          <OrganisationSelectionContent
            organisations={organisations}
            onSelect={handleOrganisationSelect}
          />
        )
      });
    },
    [showInfoModal, handleOrganisationSelect]
  );

  const noOrganisationsAvailable = useCallback(() => {
    showInfoModal({
      title: "Access Denied",
      children: <AccessDeniedContent />,
      actions: [
        {
          name: "signOut",
          type: "primary",
          text: "OK"
        }
      ],
      onAction: (actionName) => {
        if (actionName === "signOut") {
          dmnIdentity.signOut();
        }
      }
    });
  }, [showInfoModal]);

  /**
   * Loads organisation data by attempting to fetch from two venue group APIs.
   * First tries /venues_group endpoint. If that returns no venue group, falls back to /venue-groups.
   * Handles various error scenarios:
   * - Both APIs fail: combines error messages
   * - One API fails: uses the successful one or shows error from failed one
   * - Empty results: shows appropriate permission error
   * - Single organisation: auto-selects it
   * - Multiple organisations: shows selection modal
   */
  const loadOrganisationData = useCallback(async () => {
    if (loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    updateStage("organisation", "loading");

    let venueGroupConfigError: Error | null = null;
    let organisationsError: Error | null = null;

    let venueGroupConfig;
    try {
      venueGroupConfig = await fetchVenueGroupConfig();
    } catch (error) {
      venueGroupConfigError =
        error instanceof Error ? error : new Error(String(error));
    }

    if (venueGroupConfig?.user) {
      setVenueUserConfig(venueGroupConfig.user);
    }

    if (venueGroupConfig?.venueGroup?._id) {
      setVenueGroupConfig(venueGroupConfig.venueGroup);
      updateStage("organisation", "success");
      loadingRef.current = false;
      setIsLoading(false);
      return venueGroupConfig;
    }

    let organisations;
    try {
      organisations = await fetchOrganisations();
    } catch (error) {
      organisationsError =
        error instanceof Error ? error : new Error(String(error));
    }

    if (venueGroupConfigError && organisationsError) {
      const combinedDetails = [
        "Both venue group APIs failed:",
        "",
        "1. GET /venues_group:",
        venueGroupConfigError.message,
        "",
        "2. GET /venue-groups:",
        organisationsError.message
      ].join("\n");

      updateStage("organisation", "error", {
        message:
          "Failed to load organisation data from both APIs. Please try again.",
        technicalDetails: combinedDetails
      });
      errorHandler(organisationsError, "Error loading organisation data");
      noOrganisationsAvailable();
      loadingRef.current = false;
      setIsLoading(false);
      return organisations;
    }

    if (organisationsError) {
      updateStage("organisation", "error", {
        message: "Failed to load organisation list. Please try again.",
        technicalDetails: organisationsError.message
      });
      errorHandler(organisationsError, "Error loading organisation data");
      noOrganisationsAvailable();
      loadingRef.current = false;
      setIsLoading(false);
      return;
    }

    if (!organisations || organisations.length === 0) {
      const details = venueGroupConfigError
        ? `GET /venues_group: ${venueGroupConfigError.message}\n\nGET /venue-groups: returned empty array`
        : "fetchOrganisations() returned empty array. User may not have allowed_orgs configured.";

      updateStage("organisation", "error", {
        message: "You don't have permission to access any organisations.",
        technicalDetails: details
      });
      noOrganisationsAvailable();
      loadingRef.current = false;
      setIsLoading(false);
      return;
    }

    if (organisations.length === 1) {
      await handleOrganisationSelect(organisations[0]._id);
      loadingRef.current = false;
      setIsLoading(false);
      return;
    }

    updateStage("organisation", "success");
    showOrganisationSelectionModal(organisations);
    loadingRef.current = false;
    setIsLoading(false);
  }, [
    setVenueGroupConfig,
    setVenueUserConfig,
    updateStage,
    handleOrganisationSelect,
    showOrganisationSelectionModal,
    noOrganisationsAvailable
  ]);

  return {
    loadOrganisationData,
    isLoading
  };
};

export default useManageOrgData;
